import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PATH_DASHBOARD } from "../../routes/paths";
import axiosInstance from "../../utils/axios";

const initialState = {
  fetchLoading: false,
  isLoading: false,
  caterings: {
    data: [],
    meta: {
      total: 0,
    },
  },
  singleCatering: null,
  cateringFormData: {
    formData: [],
    meta: {
      total: 0,
    },
  },
};

export const createCatering = createAsyncThunk(
  "createCatering/cateringItems",
  async ({ data, enqueueSnackbar, navigate }, thunkApi) => {
    try {
      const response = await axiosInstance.post("catering-items", data);
      enqueueSnackbar("Catering is added successfully");
      navigate(PATH_DASHBOARD.contents.catering.list);
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchCaterings = createAsyncThunk(
  "fetchCaterings/cateringItems",
  async ({ limit, page }, thunkApi) => {
    try {
      const response = await axiosInstance.get(
        `catering-items?limit=${limit}&page=${page + 1}`
      );
      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

export const fetchSingleCatering = createAsyncThunk(
  "fetchSingleCatering/cateringItems",
  async ({ slug }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`catering-items/${slug}`);
      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

export const updateCatering = createAsyncThunk(
  "updateCatering/cateringItems",
  async ({ slug, data, enqueueSnackbar, navigate }, thunkApi) => {
    try {
      const response = await axiosInstance.post(
        `catering-items/${slug}?_method=PUT`,
        data
      );
      return {
        data: response.data.data,
        enqueueSnackbar,
        slug,
        navigate,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const deleteCatering = createAsyncThunk(
  "deleteCatering/cateringItems",
  async ({ id, enqueueSnackbar, limit, page }, thunkApi) => {
    try {
      await axiosInstance.delete(`catering-items/${id}`);
      thunkApi.dispatch(fetchCaterings({ limit, page }));
      return { id, enqueueSnackbar };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchCateringFormData = createAsyncThunk(
  "fetchCateringFormData/catering",
  async ({ limit, page }, thunkApi) => {
    try {
      const response = await axiosInstance.get(
        `caterings?limit=${limit}&page=${page + 1}`
      );
      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error });
    }
  }
);

const cateringSlice = createSlice({
  name: "catering",
  initialState,
  extraReducers: (builder) => {
    // TODO: create category
    builder.addCase(createCatering.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(createCatering.fulfilled, (state, action) => {
      state.isLoading = false;
      state.caterings.data = [action.payload, ...state.caterings.data];
      state.caterings.meta.total = state.caterings.meta.total + 1;
    });

    builder.addCase(createCatering.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch catering
    builder.addCase(fetchCaterings.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchCaterings.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.caterings = action.payload;
    });

    builder.addCase(fetchCaterings.rejected, (state, action) => {
      state.fetchLoading = false;
    });

    // ==========================

    // TODO: fetch single catering
    builder.addCase(fetchSingleCatering.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchSingleCatering.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.singleCatering = action.payload;
    });

    builder.addCase(fetchSingleCatering.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: update catering
    builder.addCase(updateCatering.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateCatering.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar("Catering is updated successfully");
      state.caterings.data = state.caterings.data.map((catering) =>
        catering.slug === action.payload.slug ? action.payload.data : catering
      );
      action.payload.navigate(PATH_DASHBOARD.contents.catering.list);
    });

    builder.addCase(updateCatering.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete category
    builder.addCase(deleteCatering.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteCatering.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar("Catering is deleted successfully");
    });

    builder.addCase(deleteCatering.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch catering form data
    builder.addCase(fetchCateringFormData.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchCateringFormData.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.cateringFormData = action.payload;
    });

    builder.addCase(fetchCateringFormData.rejected, (state, action) => {
      state.fetchLoading = false;
    });
  },
});

export default cateringSlice.reducer;
